import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { getApiBaseUrl } from '@/lib/api'
import { getAuthHeaders } from '@/services/api'
import { useProgramContext } from '@/context/ProgramContext'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import {
	Dialog as ScreenshotDialog,
	DialogContent as ScreenshotDialogContent,
	DialogTrigger as ScreenshotDialogTrigger,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import { formatDistanceToNow, format } from 'date-fns'
import {
	Globe,
	Laptop,
	Smartphone,
	Camera,
	X,
	AlertTriangle,
	Maximize2,
	CheckCircle,
	User,
	Settings,
	Calendar,
	Send,
	MessageCircle,
	Loader2,
	ChevronDown,
	ChevronUp,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { VisuallyHidden } from '@/components/ui/visually-hidden'
import { useTranslation } from '@/hooks/useTranslation'
import { useSendReplyShortcut } from '@/hooks/useSendReplyShortcut'
import { AiDraftAssistantPanel } from '@/components/ai/AiDraftAssistantPanel'
import { SendReplyShortcutHint } from '@/components/common/SendReplyShortcutHint'
import { useTeamContext } from '@/context/TeamContext'

interface UserMetadata {
	browser?: string
	browserVersion?: string
	os?: string
	osVersion?: string
	deviceVendor?: string
	deviceModel?: string
	deviceType?: string
	screenWidth?: number
	screenHeight?: number
	pageUrl?: string
	pageTitle?: string
	userAgent?: string
	parsedUA?: {
		os?: {
			name?: string
			version?: string
		}
		browser?: {
			name?: string
			version?: string
			major?: string
		}
		device?: {
			vendor?: string
			model?: string
			type?: string
		}
	}
}

interface BugReport {
	id: string
	description: string
	status: string
	severity: string
	widget: { name: string } | null
	program: { name: string } | null
	reporter_name: string | null
	reporter_email: string | null
	external_user_id: string | null
	team_id: string | null
	user_metadata: UserMetadata | null
	user_properties: Record<string, any> | null
	screenshot_url: string | null
	created_at: string
	updated_at: string
	assignee_user_id?: string | null
	widget_id?: string | null
	program_id?: string | null
	internal_notes?: string | null
}

interface BugConversation {
	id: string
	bug_report_id: string
	sender_type: 'support' | 'customer'
	sender_user_id: string | null
	sender_name: string | null
	sender_email: string | null
	message: string
	created_at: string
	email_message_id: string | null
	in_reply_to: string | null
}

interface FeedbackHistory {
	id: string
	feedback_type: string
	feedback_id: string
	field_name: 'status' | 'severity' | 'internal_notes'
	old_value: string | null
	new_value: string
	changed_by_user_id: string | null
	changed_by_team_member_id: string | null
	created_at: string
	changed_by_name?: string
}

interface BugReportDetailViewProps {
	bugId: string | null
	onClose: () => void
}

export function BugReportDetailView({
	bugId,
	onClose,
}: BugReportDetailViewProps) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const { currentProgramAccessLevel } = useProgramContext()
	const { teamId: contextTeamId } = useTeamContext()
	const [replyMessage, setReplyMessage] = useState('')
	const [isSendingReply, setIsSendingReply] = useState(false)
	const [internalNotes, setInternalNotes] = useState('')
	const [isInternalNotesExpanded, setIsInternalNotesExpanded] = useState(false)
	const [appliedAiDraft, setAppliedAiDraft] = useState<string | null>(null)
	const [aiSuggestionEventId, setAiSuggestionEventId] = useState<string | null>(null)

	const getModalTitle = (bugData: BugReport | undefined) => {
		if (!bugData) return t('bugReportDetailView.bugReportDetails')
		const wordLimit = window.innerWidth >= 768 ? 12 : 7
		const shortDescription = bugData.description
			.split(' ')
			.slice(0, wordLimit)
			.join(' ')
		if (bugData.description.length > shortDescription.length) {
			return `${t('bugReportDetailView.bugPrefix')} ${shortDescription}...`
		}
		return `${t('bugReportDetailView.bugPrefix')} ${shortDescription}`
	}
	const isOpen = bugId !== null

	const handleOpenChange = (open: boolean) => {
		if (!open) {
			onClose()
		}
	}

	const { data: bug, isLoading } = useQuery<BugReport>({
		queryKey: ['bug', bugId],
		queryFn: async () => {
			if (!bugId) throw new Error('Bug ID is required')
			const { data, error } = await supabase
				.from('bug_reports')
				.select('*, external_user_id, user_properties, widget:widget_id(name), program:program_id(name)')
				.eq('id', bugId)
				.single()

			if (error) {
				console.error('Error fetching bug details:', error)
				throw error
			}
			return data as BugReport
		},
		enabled: !!bugId,
	})

	useEffect(() => {
		if (bug) {
			setInternalNotes(bug.internal_notes || '')
			// Expand by default if internal notes are filled, collapse if empty
			setIsInternalNotesExpanded(!!bug.internal_notes)
		}
	}, [bug?.id]) // Only reset when bug ID changes, not on every update

	useEffect(() => {
		if (!bug) return

		// Don't save if notes haven't changed
		if (internalNotes === (bug.internal_notes || '')) return

		const timer = setTimeout(() => {
			updateBugMutation.mutate({ internal_notes: internalNotes })
		}, 1000)

		return () => clearTimeout(timer)
	}, [internalNotes])

	const { data: conversations = [], isLoading: isLoadingConversations } = useQuery<BugConversation[]>({
		queryKey: ['bug-conversations', bugId],
		queryFn: async () => {
			if (!bugId) throw new Error('Bug ID is required')
			const response = await fetch(`${getApiBaseUrl()}/api/bugs/protected/${bugId}/conversations`, {
				headers: await getAuthHeaders(),
			})

			if (!response.ok) {
				throw new Error('Failed to fetch conversations')
			}

			const data = await response.json()
			return data.conversations || []
		},
		enabled: !!bugId,
		refetchInterval: 30000,
	})

	const { data: history = [], isLoading: isLoadingHistory } = useQuery<FeedbackHistory[]>({
		queryKey: ['bug-history', bugId],
		queryFn: async () => {
			if (!bugId) throw new Error('Bug ID is required')
			const response = await fetch(`${getApiBaseUrl()}/api/bugs/protected/${bugId}/history`, {
				headers: await getAuthHeaders(),
			})

			if (!response.ok) {
				throw new Error('Failed to fetch history')
			}

			const data = await response.json()
			return data.history || []
		},
		enabled: !!bugId,
	})

	const canSendReply =
		!!replyMessage.trim() &&
		!isSendingReply &&
		currentProgramAccessLevel !== 'read'

	const { onKeyDown: onReplyKeyDown, shortcutLabel } = useSendReplyShortcut(
		handleSendReply,
		{ canSend: canSendReply, isSending: isSendingReply }
	)

	async function handleSendReply() {
		if (!bugId || !replyMessage.trim()) return

		setIsSendingReply(true)
		try {
			const response = await fetch(`${getApiBaseUrl()}/api/bugs/protected/${bugId}/reply`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					...await getAuthHeaders(),
				},
				body: JSON.stringify({
				message: replyMessage,
				aiSuggestionEventId: appliedAiDraft ? aiSuggestionEventId : null,
				aiEdited: appliedAiDraft ? replyMessage !== appliedAiDraft : false,
			}),
			})

			if (!response.ok) {
				const contentType = response.headers.get('content-type')
				let errorMessage = 'Failed to send reply'

				if (contentType && contentType.includes('application/json')) {
					try {
						const error = await response.json()
						errorMessage = error.error || errorMessage
					} catch (e) {
						console.error('Failed to parse error response:', e)
					}
				} else {
					const text = await response.text()
					console.error('Non-JSON error response:', text.substring(0, 200))
					errorMessage = `Server error (${response.status}): ${response.statusText}`
				}

				throw new Error(errorMessage)
			}

			const data = await response.json()
			setReplyMessage('')
			setAppliedAiDraft(null)
			setAiSuggestionEventId(null)
			queryClient.invalidateQueries({ queryKey: ['bug-conversations', bugId] })
		} catch (error) {
			console.error('Error sending reply:', error)
			const message = error instanceof Error ? error.message : 'Failed to send reply. Please try again.'
			alert(message)
		} finally {
			setIsSendingReply(false)
		}
	}

	const updateBugMutation = useMutation({
		mutationFn: async (data: Partial<BugReport>) => {
			if (!bugId) throw new Error('Bug ID is required for update')

			// Use the protected API endpoint with proper access control
			const response = await fetch(`${getApiBaseUrl()}/api/bugs/protected/${bugId}`, {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
					...await getAuthHeaders(),
				},
				body: JSON.stringify(data),
			})

			if (!response.ok) {
				const error = await response.json()
				throw new Error(error.error || 'Failed to update bug report')
			}

			return response.json()
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['bug', bugId] })
			queryClient.invalidateQueries({ queryKey: ['bugs'] })
			queryClient.invalidateQueries({ queryKey: ['dashboardData'] })
			queryClient.invalidateQueries({ queryKey: ['bug-history', bugId] })
			queryClient.invalidateQueries({
				queryKey: ['recentWidgetData', bug?.widget_id, bug?.program_id, 'bugs'],
			})
		},
	})

	if (isLoading || (isOpen && !bug)) {
		return (
			<Dialog open={isOpen} onOpenChange={handleOpenChange}>
				<DialogContent className="sm:max-w-xl md:max-w-2xl overflow-y-auto max-h-[90vh]">
					<DialogHeader>
						<Skeleton className="h-6 w-3/4" />
					</DialogHeader>
					<div className="space-y-6 py-4">
						<Skeleton className="h-8 w-full" />
						<Skeleton className="h-32 w-full" />
						<Skeleton className="h-20 w-full" />
						<Skeleton className="h-20 w-full" />
					</div>
				</DialogContent>
			</Dialog>
		)
	}

	if (!isOpen || !bug) return null

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogContent
				aria-describedby={bug ? `bug-report-description-${bug.id}` : undefined}
				className="sm:max-w-2xl md:max-w-4xl lg:max-w-6xl overflow-y-auto max-h-[90vh]">
				<DialogHeader className="space-y-2 pr-8">
					<div className="flex items-center justify-between">
						<DialogTitle
							className="text-xl font-semibold truncate"
							title={bug.description}>
							🐛 {getModalTitle(bug)}
						</DialogTitle>
					</div>
					<VisuallyHidden>
						<DialogDescription
							id={bug ? `bug-report-description-${bug.id}` : undefined}>
							{t('bugReportDetailView.detailsForBugReport')} #{bug.id.slice(0, 8)}. {t('bugReportTable.status')}: {bug.status}
							, {t('bugReportTable.severity')}: {bug.severity}.
						</DialogDescription>
					</VisuallyHidden>
					<div className="flex gap-3 pt-2">
						<div className="flex items-center gap-2">
							<label className="text-xs font-medium text-muted-foreground">{t('bugReportTable.status')}:</label>
							<Select
								value={bug.status}
								onValueChange={(value) =>
									updateBugMutation.mutate({
										status: value,
									})
								}
								disabled={currentProgramAccessLevel === 'read'}>
								<SelectTrigger className={`w-32 h-7 text-xs ${bug.status === 'new'
										? 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900 dark:text-blue-300 dark:border-blue-800'
										: bug.status === 'in_progress'
											? 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900 dark:text-yellow-300 dark:border-yellow-800'
											: bug.status === 'resolved' || bug.status === 'closed'
												? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900 dark:text-green-300 dark:border-green-800'
												: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'
									}`}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="new">{t('bugReportTable.statusNew')}</SelectItem>
									<SelectItem value="in_progress">{t('bugReportTable.statusInProgress')}</SelectItem>
									<SelectItem value="resolved">{t('bugReportTable.statusResolved')}</SelectItem>
									<SelectItem value="closed">{t('bugReportTable.statusClosed')}</SelectItem>
									<SelectItem value="archived">{t('bugReportTable.statusArchived')}</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="flex items-center gap-2">
							<label className="text-xs font-medium text-muted-foreground">{t('bugReportTable.severity')}:</label>
							<Select
								value={bug.severity}
								onValueChange={(value) =>
									updateBugMutation.mutate({
										severity: value,
									})
								}
								disabled={currentProgramAccessLevel === 'read'}>
								<SelectTrigger className={`w-24 h-7 text-xs ${bug.severity === 'critical'
										? 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900 dark:text-red-300 dark:border-red-800'
										: bug.severity === 'high'
											? 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900 dark:text-orange-300 dark:border-orange-800'
											: bug.severity === 'medium'
												? 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900 dark:text-amber-300 dark:border-amber-800'
												: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'
									}`}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="critical">{t('bugReportTable.severityCritical')}</SelectItem>
									<SelectItem value="high">{t('bugReportTable.severityHigh')}</SelectItem>
									<SelectItem value="medium">{t('bugReportTable.severityMedium')}</SelectItem>
									<SelectItem value="low">{t('bugReportTable.severityLow')}</SelectItem>
								</SelectContent>
							</Select>
						</div>
						{bug.widget?.name && (
							<Badge variant="outline">{bug.widget.name}</Badge>
						)}
					</div>
				</DialogHeader>

				<Separator className="my-2" />

				<div className="space-y-6">
					<div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
						{/* Main Content - Left Column */}
						<div className="lg:col-span-8 space-y-6">


							<div className="space-y-3">
								<h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
									<div className="h-4 w-4 flex items-center justify-center bg-primary/20 dark:bg-primary/30 rounded-full">
										<svg className="h-2.5 w-2.5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
										</svg>
									</div>
									{t('bugReportTable.description')}
								</h3>
								<div className="bg-muted/50 p-4 rounded-lg min-h-[120px] whitespace-pre-wrap text-sm">
									{bug.description}
								</div>
							</div>
							{/* Internal Notes */}
							<div className="space-y-3">
								<button
									type="button"
									onClick={() => setIsInternalNotesExpanded(!isInternalNotesExpanded)}
									className="text-sm font-medium text-muted-foreground flex items-center justify-between w-full hover:text-foreground transition-colors"
								>
									<div className="flex items-center gap-2">
										<div className="h-4 w-4 flex items-center justify-center bg-tertiary/80 rounded-full">
											<Settings className="h-2.5 w-2.5 text-primary" />
										</div>
										{t('bugReportDetailView.internalNotes', 'Internal Notes')}
									</div>
									<div className="flex items-center gap-2">
										{updateBugMutation.isPending && (
											<div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
												<Loader2 className="h-2.5 w-2.5 animate-spin" />
												{t('bugReportDetailView.saving', 'Saving...')}
											</div>
										)}
										{!updateBugMutation.isPending && updateBugMutation.isSuccess && (
											<div className="flex items-center gap-1.5 text-[10px] text-emerald-500 font-medium">
												<CheckCircle className="h-2.5 w-2.5" />
												{t('bugReportDetailView.saved', 'Saved')}
											</div>
										)}
										{isInternalNotesExpanded ? (
											<ChevronUp className="h-4 w-4" />
										) : (
											<ChevronDown className="h-4 w-4" />
										)}
									</div>
								</button>
								{isInternalNotesExpanded && (
									<div className="space-y-2">
										<Textarea
											value={internalNotes}
											onChange={(e) => setInternalNotes(e.target.value)}
											placeholder={t('bugReportDetailView.internalNotesPlaceholder', 'Add private context or updates for your team...')}
											className="min-h-[100px] text-sm bg-muted/50 focus:bg-background transition-colors"
											disabled={currentProgramAccessLevel === 'read'}
										/>
										<p className="text-[10px] text-muted-foreground italic px-1">
											{t('bugReportDetailView.internalNotesHint', 'Notes are private to your team and saved automatically as you type.')}
										</p>
									</div>
								)}
							</div>

							{bug.reporter_email && (
								<div className="space-y-3">
									<h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
										<div className="h-4 w-4 flex items-center justify-center bg-primary/20 dark:bg-primary/30 rounded-full">
											<MessageCircle className="h-2.5 w-2.5 text-primary" />
										</div>
										{t('bugReportDetailView.conversation', 'Conversation')}
									</h3>

									<div className="space-y-3">
										{isLoadingConversations ? (
											<div className="flex justify-center py-4">
												<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
											</div>
										) : conversations.length > 0 ? (
											<div className="space-y-3">
												{conversations.map((conv) => (
													<Card key={conv.id} className={`p-4 ${conv.sender_type === 'support' ? 'bg-blue-50 dark:bg-blue-950/20' : 'bg-muted/50'}`}>
														<div className="flex items-start gap-3">
															<div className={`h-8 w-8 rounded-full flex items-center justify-center ${conv.sender_type === 'support' ? 'bg-blue-100 dark:bg-blue-900' : 'bg-gray-200 dark:bg-gray-700'}`}>
																{conv.sender_type === 'support' ? (
																	<User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
																) : (
																	<User className="h-4 w-4 text-gray-600 dark:text-gray-400" />
																)}
															</div>
															<div className="flex-1 space-y-1">
																<div className="flex items-center justify-between">
																	<span className="text-sm font-medium">
																		{conv.sender_name || (conv.sender_type === 'support' ? 'Support' : 'Customer')}
																	</span>
																	<span className="text-xs text-muted-foreground">
																		{formatDistanceToNow(new Date(conv.created_at), { addSuffix: true })}
																	</span>
																</div>
																<p className="text-sm whitespace-pre-wrap">{conv.message}</p>
															</div>
														</div>
													</Card>
												))}
											</div>
										) : (
											<div className="text-center py-6 text-sm text-muted-foreground">
												{t('bugReportDetailView.noConversation', 'No messages yet. Send a reply to start the conversation.')}
											</div>
										)}
									</div>

									{currentProgramAccessLevel !== 'read' && (
										<div className="space-y-3">
											<AiDraftAssistantPanel
												teamId={bug.team_id || contextTeamId}
												programId={bug.program_id}
												feedbackType="bugs"
												feedbackId={bugId}
												canWrite={currentProgramAccessLevel !== 'read'}
												onApplyDraft={(draft, eventId) => {
													setReplyMessage(draft)
													setAppliedAiDraft(draft)
													setAiSuggestionEventId(eventId)
												}}
												onSendReply={async (message, eventId, wasEdited) => {
													if (!bugId || !message.trim()) return
													setIsSendingReply(true)
													try {
														const response = await fetch(`${getApiBaseUrl()}/api/bugs/protected/${bugId}/reply`, {
															method: 'POST',
															headers: {
																'Content-Type': 'application/json',
																...await getAuthHeaders(),
															},
															body: JSON.stringify({
																message,
																aiSuggestionEventId: eventId,
																aiEdited: wasEdited,
															}),
														})
														if (!response.ok) {
															const contentType = response.headers.get('content-type')
															let errorMessage = 'Failed to send reply'
															if (contentType && contentType.includes('application/json')) {
																try {
																	const error = await response.json()
																	errorMessage = error.error || errorMessage
																} catch (e) {
																	console.error('Failed to parse error response:', e)
																}
															}
															throw new Error(errorMessage)
														}
														setReplyMessage('')
														setAppliedAiDraft(null)
														setAiSuggestionEventId(null)
														queryClient.invalidateQueries({ queryKey: ['bug-conversations', bugId] })
													} catch (error) {
														console.error('Error sending reply:', error)
														const msg = error instanceof Error ? error.message : 'Failed to send reply. Please try again.'
														alert(msg)
														throw error
													} finally {
														setIsSendingReply(false)
													}
												}}
												isSendingReply={isSendingReply}
												onSuggestionCleared={() => {
													setAppliedAiDraft(null)
													setAiSuggestionEventId(null)
												}}
												resetToken={bugId}
												showKnowledgeBaseLink
											/>
											<Textarea
												placeholder={t('bugReportDetailView.typeReply', 'Type your reply...')}
												value={replyMessage}
												onChange={(e) => setReplyMessage(e.target.value)}
												onKeyDown={onReplyKeyDown}
												className="min-h-[100px]"
												disabled={isSendingReply}
											/>
											<div className="flex justify-end">
												<Button
													onClick={handleSendReply}
													disabled={!replyMessage.trim() || isSendingReply}
													size="sm"
												>
													<Send className="h-4 w-4 mr-2" />
													{isSendingReply ? t('bugReportDetailView.sending', 'Sending...') : t('bugReportDetailView.sendReply', 'Send Reply')}
													{!isSendingReply && <SendReplyShortcutHint label={shortcutLabel} />}
												</Button>
											</div>
										</div>
									)}
								</div>
							)}

							{history.length > 0 && (
								<div className="space-y-3 mt-6">
									<h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
										<div className="h-4 w-4 flex items-center justify-center bg-primary/20 dark:bg-primary/30 rounded-full">
											<Calendar className="h-2.5 w-2.5 text-primary" />
										</div>
										{t('bugReportDetailView.history', 'History')}
									</h3>
									<div className="bg-muted/50 p-4 rounded-lg space-y-3">
										{isLoadingHistory ? (
											<div className="flex justify-center py-4">
												<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
											</div>
										) : (
											history.map((item) => (
												<div key={item.id} className="flex items-start gap-3 pb-3 border-b last:border-b-0 last:pb-0">
													<div className="h-8 w-8 flex items-center justify-center flex-shrink-0">
														<Settings className="h-4 w-4 text-primary" />
													</div>
													<div className="flex-1 space-y-1">
														<div className="flex items-center justify-between">
															<span className="text-sm font-medium">
																{item.field_name === 'status'
																	? t('bugReportDetailView.statusChanged', 'Status changed')
																	: item.field_name === 'severity'
																		? t('bugReportDetailView.severityChanged', 'Severity changed')
																		: t('bugReportDetailView.internalNotesUpdated', 'Internal notes updated')}
															</span>
															<span className="text-xs text-muted-foreground">
																{formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
															</span>
														</div>
														<div className="text-sm">
															<span className="text-muted-foreground">
																{item.old_value || t('bugReportDetailView.none', 'None')}
															</span>
															<span className="mx-2">→</span>
															<span className="font-medium">{item.new_value}</span>
														</div>
														{item.changed_by_name && (
															<div className="text-xs text-muted-foreground">
																{t('bugReportDetailView.changedBy', 'Changed by')} {item.changed_by_name}
															</div>
														)}
													</div>
												</div>
											))
										)}
									</div>
								</div>
							)}
						</div>


						{/* Sidebar - Right Column with vertical separator */}
						<div className="lg:col-span-4 lg:border-l lg:border-border lg:pl-6 space-y-6">
							{bug.screenshot_url && (
								<div className="space-y-3">
									<h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
										<div className="h-4 w-4 flex items-center justify-center bg-primary/20 dark:bg-primary/30 rounded-full">
											<Camera className="h-2.5 w-2.5 text-primary" />
										</div>
										{t('bugReportTable.screenshot')}
									</h3>
									<ScreenshotDialog>
										<ScreenshotDialogTrigger asChild>
											<div className="relative aspect-video rounded-lg border overflow-hidden cursor-pointer group bg-muted">
												<img
													src={bug.screenshot_url}
													alt={t('bugReportDetailView.bugReportScreenshot')}
													className="object-contain w-full h-full transition-transform group-hover:scale-[1.02]"
												/>
												<div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
													<Maximize2 className="h-6 w-6 text-white opacity-0 group-hover:opacity-75 transition-opacity" />
												</div>
											</div>
										</ScreenshotDialogTrigger>
										<ScreenshotDialogContent className="max-w-[90vw] max-h-[90vh] w-screen h-screen p-0 bg-black/95 border-none shadow-none flex items-center justify-center [&_button_svg]:text-white [&_button]:absolute [&_button]:top-4 [&_button]:right-4 [&_button]:z-50">
											<img
												src={bug.screenshot_url}
												alt={t('bugReportDetailView.bugReportScreenshot')}
												className="max-w-full max-h-full object-contain"
											/>
										</ScreenshotDialogContent>
									</ScreenshotDialog>
								</div>
							)}

							<Separator className="my-2" />

							<div className="space-y-3">
								<h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
									<div className="h-4 w-4 flex items-center justify-center bg-primary/20 dark:bg-primary/30 rounded-full">
										<User className="h-2.5 w-2.5 text-primary" />
									</div>
									{t('bugReportDetailView.reporterDetails')}
								</h3>
								<div className="bg-muted/50 p-4 rounded-lg space-y-3">
									<div className="flex justify-between items-center">
										<span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
											<div className="h-3 w-3 flex items-center justify-center">
												<User className="h-2 w-2 text-primary" />
											</div>
											{t('bugReportDetailView.name')}:
										</span>
										<div className="flex items-center gap-2">
											<span className="text-xs text-foreground font-medium">
												{bug.reporter_name || t('bugReportTable.anonymous')}
											</span>
											{bug.external_user_id && (
												<Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300 text-xs">
													<CheckCircle className="h-3 w-3 mr-1" />
													{t('bugReportDetailView.verified')}
												</Badge>
											)}
										</div>
									</div>

									{bug.reporter_email && (
										<div className="flex justify-between items-center">
											<span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
												<div className="h-3 w-3 flex items-center justify-center">
													<svg className="h-2 w-2 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
													</svg>
												</div>
												{t('bugReportDetailView.email')}:
											</span>
											<a
												href={`mailto:${bug.reporter_email}`}
												className="text-xs text-foreground font-medium hover:text-primary transition-colors">
												{bug.reporter_email}
											</a>
										</div>
									)}

									<div className="flex justify-between items-center">
										<span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
											<div className="h-3 w-3 flex items-center justify-center">
												<Calendar className="h-2 w-2 text-primary" />
											</div>
											{t('bugReportDetailView.created')}:
										</span>
										<span className="text-xs text-foreground font-medium">
											{format(new Date(bug.created_at), 'MMM d, yyyy')} {t('bugReportDetailView.at')} {format(new Date(bug.created_at), 'h:mm a')}
										</span>
									</div>

									<div className="flex justify-between items-center">
										<span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
											<div className="h-3 w-3 flex items-center justify-center">
												<Calendar className="h-2 w-2 text-primary" />
											</div>
											{t('bugReportDetailView.updated')}:
										</span>
										<span className="text-xs text-foreground font-medium">
											{format(new Date(bug.updated_at), 'MMM d, yyyy')} {t('bugReportDetailView.at')} {format(new Date(bug.updated_at), 'h:mm a')}
										</span>
									</div>

									{bug.external_user_id && (
										<div className="flex justify-between items-start">
											<span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
												<div className="h-3 w-3 flex items-center justify-center">
													<svg className="h-2 w-2 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
													</svg>
												</div>
												{t('bugReportDetailView.userId')}:
											</span>
											<div className="flex-1 flex justify-end ml-2">
												<code className="text-foreground font-medium text-xs text-right">
													{bug.external_user_id}
												</code>
											</div>
										</div>
									)}

									{bug.user_properties && Object.entries(bug.user_properties).map(([key, value]) => (
										<div key={key} className="flex justify-between items-center">
											<span className="text-xs font-medium text-muted-foreground capitalize flex items-center gap-1">
												<div className="h-3 w-3 flex items-center justify-center">
													<svg className="h-2 w-2 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
													</svg>
												</div>
												{key.replace(/([A-Z])/g, ' $1')}:
											</span>
											<div className="flex-1 flex justify-end ml-2 text-xs">
												<span className="text-foreground font-medium text-right">{String(value)}</span>
											</div>
										</div>
									))}
								</div>
							</div>

							<Separator className="my-2" />
							{bug.user_metadata && (
								<div className="space-y-3">
									<h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
										<div className="h-4 w-4 flex items-center justify-center bg-primary/20 dark:bg-primary/30 rounded-full">
											<Globe className="h-2.5 w-2.5 text-primary" />
										</div>
										{t('bugReportDetailView.techContext')}
									</h3>
									<div className="bg-muted/50 p-4 rounded-lg space-y-3">
										{(bug.user_metadata.parsedUA?.os?.name || bug.user_metadata.os) && (
											<div className="flex justify-between items-center">
												<span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
													<div className="h-3 w-3 flex items-center justify-center">
														<Laptop className="h-2 w-2 text-primary" />
													</div>
													{t('bugReportDetailView.os')}:
												</span>
												<span className="text-xs text-foreground font-medium">
													{bug.user_metadata.parsedUA?.os?.name
														? `${bug.user_metadata.parsedUA.os.name}${bug.user_metadata.parsedUA.os.version ? ` ${bug.user_metadata.parsedUA.os.version}` : ''}`
														: `${bug.user_metadata.os}${bug.user_metadata.osVersion ? ` ${bug.user_metadata.osVersion}` : ''}`
													}
												</span>
											</div>
										)}

										{(bug.user_metadata.parsedUA?.browser?.name || bug.user_metadata.browser) && (
											<div className="flex justify-between items-center">
												<span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
													<div className="h-3 w-3 flex items-center justify-center">
														<Globe className="h-2 w-2 text-primary" />
													</div>
													{t('bugReportDetailView.browser')}:
												</span>
												<span className="text-xs text-foreground font-medium">
													{bug.user_metadata.parsedUA?.browser?.name
														? `${bug.user_metadata.parsedUA.browser.name}${bug.user_metadata.parsedUA.browser.version ? ` ${bug.user_metadata.parsedUA.browser.version}` : ''}`
														: `${bug.user_metadata.browser}${bug.user_metadata.browserVersion ? ` ${bug.user_metadata.browserVersion}` : ''}`
													}
												</span>
											</div>
										)}

										{(bug.user_metadata.screenWidth && bug.user_metadata.screenHeight) && (
											<div className="flex justify-between items-center">
												<span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
													<div className="h-3 w-3 flex items-center justify-center">
														<Smartphone className="h-2 w-2 text-primary" />
													</div>
													{t('bugReportDetailView.resolution')}:
												</span>
												<span className="text-xs text-foreground font-medium">
													{bug.user_metadata.screenWidth}x{bug.user_metadata.screenHeight}
												</span>
											</div>
										)}

										{bug.user_metadata.pageUrl && (
											<div className="flex justify-between items-start">
												<span className="text-xs font-medium text-muted-foreground flex items-center gap-1 shrink-0">
													<div className="h-3 w-3 flex items-center justify-center">
														<svg className="h-2 w-2 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
														</svg>
													</div>
													{t('bugReportDetailView.pageUrl')}:
												</span>
												<div className="flex-1 flex justify-end ml-2">
													<a
														href={bug.user_metadata.pageUrl}
														target="_blank"
														rel="noopener noreferrer"
														className="text-xs text-primary hover:underline break-all text-right">
														{bug.user_metadata.pageUrl}
													</a>
												</div>
											</div>
										)}

										{bug.user_metadata.userAgent && (
											<div className="flex justify-between items-start">
												<span className="text-xs font-medium text-muted-foreground flex items-center gap-1 shrink-0">
													<div className="h-3 w-3 flex items-center justify-center">
														<svg className="h-2 w-2 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
														</svg>
													</div>
													{t('bugReportDetailView.userAgent')}:
												</span>
												<div className="flex-1 flex justify-end ml-2">
													<span className="text-xs text-foreground font-medium break-all text-right">
														{bug.user_metadata.userAgent}
													</span>
												</div>
											</div>
										)}
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
